<template>
  <div :class="$style.box">
    <SectionTitle :class="$style.box__title">パンフレット</SectionTitle>
    <div :class="$style.box__content">
      <DocumentList
        :corporations="filteredCorporations"
        @click="clickDocument"
      />

      <DocumentModal
        :isOpened="isModalOpened"
        :fileUrl="fileUrl"
        @close="closeModal"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import {
  type PamphletViewCorporationFragment,
  type PamphletViewDocumentFragment,
  useGetPamphletViewDocumentFileUrlLazyQuery,
} from './PamphletView.generated'
import DocumentList, {
  type Corporation,
  type Document,
} from './DocumentList.vue'
import DocumentModal from './DocumentModal.vue'
import { computed, ref } from 'vue'
import { getEntitiesByUnionArray } from '@/libs/utilities/graphql'
import { useLogger } from '@/logger/logger'

const props = defineProps<{
  corporations: PamphletViewCorporationFragment[]
}>()

const fileUrl = ref<string | null>(null)
const isModalOpened = ref<boolean>(false)

const logger = useLogger()

const closeModal = () => {
  fileUrl.value = null
  isModalOpened.value = false
}

const { load, onResult } = useGetPamphletViewDocumentFileUrlLazyQuery({
  ids: [],
})

onResult(result => {
  fileUrl.value =
    result.data?.solicitorCloudPamphletDocumentVersionByIds?.[0].fileUrl ?? null
})

const clickDocument = async ({
  documentVersionId: id,
}: {
  documentVersionId: PamphletViewDocumentFragment['id']
}) => {
  isModalOpened.value = true

  load(
    undefined,
    {
      ids: [id],
    },
    {
      fetchPolicy: 'no-cache',
    }
  )

  logger.ltlPeople.event.clickSolicitorCloudDocument(logger.buildMeta(), {
    data: { id, type: 'pamphlet-document' },
  })
}

const filteredCorporations = computed<Corporation[]>((): Corporation[] => {
  return props.corporations.reduce<Corporation[]>(
    (accumulator, { documents, ...corporation }) => {
      const convertedDocuments =
        getEntitiesByUnionArray<PamphletViewDocumentFragment>(
          documents,
          'SolicitorCloudPamphletDocument'
        )
          .filter(({ currentDocumentVersion }) => currentDocumentVersion)
          .map((item): Document => ({
            documentVersionId: item.currentDocumentVersion!.id,
            title: item.title,
            page: 0,
            publishedAt: item.currentDocumentVersion!.publishedAt,
          }))

      return convertedDocuments.length > 0
        ? [
            ...accumulator,
            {
              ...corporation,
              documents: convertedDocuments,
            },
          ]
        : accumulator
    },
    []
  )
})
</script>

<style lang="scss" module>
.box {
  padding: 20px;
  border-radius: 10px;
  width: 100%;
  background-color: $color-surface-primary;

  &__content {
    display: flex;
    flex-direction: column;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid $color-border-low-emphasis;
  }
}
</style>
